import { Data, Effect, Schema } from "effect";

export const AnnotationId = Schema.String.pipe(Schema.brand("AnnotationId"));
export type AnnotationId = typeof AnnotationId.Type;

export const SuggestionId = Schema.String.pipe(Schema.brand("SuggestionId"));
export type SuggestionId = typeof SuggestionId.Type;

// An Expert-authored edit proposal's stable id, minted server-side by the `propose-edit` op. A
// distinct namespace from `SuggestionId`: an `EditProposal` is the Expert's own change (propose →
// confirm), not an agent suggestion produced over kept annotations, so its id can never be confused
// with a suggestion id at a gate or in the bytes.
export const EditProposalId = Schema.String.pipe(Schema.brand("EditProposalId"));
export type EditProposalId = typeof EditProposalId.Type;

// The canonical bare rule id (`6.3`, `11.1`, `P.1`, `G.1`) a citation collapses to (see
// `canonicalizeRuleId`). A brand so a rule cohort key cannot be confused with arbitrary citation
// free text.
export const RuleId = Schema.String.pipe(Schema.brand("RuleId"));
export type RuleId = typeof RuleId.Type;

// A stable identity for one agent run (a "session"), minted at the call site (the review controller,
// the classify runner, the backtest op) as a branded UUID. A brand so a run identity cannot be
// confused with the SDK's own `session_id` (which becomes recorded metadata, and which sub-agents
// fork) or with arbitrary text. Every run has one — it is what the sessions list and SSE tail address.
export const RunId = Schema.String.pipe(Schema.brand("RunId"));
export type RunId = typeof RunId.Type;

// A stable identity for one **Conversation** — a group of `kind:"chat"` runs sharing the same grouping
// key (D6, ADR-0038 amendment). Minted client-side (`crypto.randomUUID()`) when the Expert starts a
// new chat, or the fixed server-minted `legacy:<documentId>` sentinel for chat runs that predate the
// `conversation-started` event. A brand so a conversation's grouping key cannot be confused with a
// `RunId` (one turn) or arbitrary text — a conversation is many runs, a run is one turn within it. A
// plain (unvalidated) brand, since the legacy sentinel form must also decode.
export const ConversationId = Schema.String.pipe(Schema.brand("ConversationId"));
export type ConversationId = typeof ConversationId.Type;

// A content hash of a rule's body text — the version key a rule is bucketed by (see
// `contentHash`). A brand so a version key cannot be confused with the rule id itself or
// with arbitrary text; an edit that changes a rule's wording yields a new `RuleContentHash`.
export const RuleContentHash = Schema.String.pipe(Schema.brand("RuleContentHash"));
export type RuleContentHash = typeof RuleContentHash.Type;

// A block's short content hash — the compare-and-swap + sibling-disambiguation token a
// `BlockLocator` carries (KTD1/KTD2/KTD2a in the block-locator refactor plan). Minted by
// `blockContentHash` (`hash.ts`), the same cyrb53 digest and whitespace normalization as
// `contentHash`. A distinct brand from `RuleContentHash`: a block's CAS token is a different
// identity namespace than a rubric rule's wording-version bucket, so the two can never be confused
// at a call site even though both are 14-hex cyrb53 digests.
export const BlockContentHash = Schema.String.pipe(Schema.brand("BlockContentHash"));
export type BlockContentHash = typeof BlockContentHash.Type;

// A `LanguagePackPath`-style traversal guard (declared here, ahead of the path brands below, so
// `DocumentId` can reuse it too): rejects an empty, absolute, null-byte, percent-encoded, or
// `.`/`..`-component path. Named generically because three brands in this file now share it.
const isPathTraversal = (s: string): boolean =>
  s.split("/").some((segment) => segment === "" || segment === "." || segment === "..");

const startsWithLanguageCode = (s: string): boolean => /^L[12]_[A-Za-z]+\//.test(s);

// ADR-0108 Decision 2 — the one pack file that legitimately sits at the ROOT of the tree rather than
// under an `L1_<code>/`/`L2_<code>/` directory: the `CODE -> display name` registry. It is the file
// that says which language codes exist, so it cannot itself live under one.
//
// Admitted by exact name rather than by relaxing `startsWithLanguageCode`, because the prefix rule is
// what keeps the brand a traversal guard: anything looser would admit arbitrary root-level paths.
// Both runtimes hard-exit when the registry is missing, so leaving it the only unstorable TEXT file
// in the tree is what blocks the tree from ever being removed (ADR-0108's blocker 4).
const LANGUAGE_PACK_ROOT_FILES: ReadonlySet<string> = new Set(["language_codes.yml"]);

// The `LanguagePackPath` shape (ADR-0097), factored out so `DocumentId` (below) can accept it as a
// second root without duplicating the traversal/encoding closure: relative, no null byte, no
// percent-encoding, no `.`/`..` component, `.md`/`.yml` only, and rooted under an `L1_<code>/` or
// `L2_<code>/` segment.
const isValidLanguagePackPath = (s: string): boolean =>
  s.length > 0 &&
  !s.startsWith("/") &&
  !s.includes("\0") &&
  !s.includes("%") &&
  !isPathTraversal(s) &&
  (s.endsWith(".md") || s.endsWith(".yml")) &&
  (startsWithLanguageCode(s) || LANGUAGE_PACK_ROOT_FILES.has(s));

// The `ReferenceGuidePath` shape (ADR-0099/ADR-0108), factored out so `DocumentId` (below) can accept
// it as a third root without duplicating the closure — the same precedent as `isValidLanguagePackPath`
// above. See `ReferenceGuidePath` further down for the full rationale on which paths this admits.
const isValidReferenceGuidePath = (s: string): boolean =>
  s === "curriculum-reference.md" ||
  s === "curriculum-quality-rubric.md" ||
  s === "exercise-quality-rubric.md" ||
  s === "cefr-descriptors-2020.yml" ||
  /^authoring-guides\/[a-z0-9][a-z0-9-]*\.md$/.test(s) ||
  /^few-shot-examples\/[a-z0-9][a-z0-9-]*\.jsonl$/.test(s) ||
  /^workflows\/[a-z0-9][a-z0-9-]*\.yml$/.test(s) ||
  // ADR-0134: one learner-facing exercise-instruction table per locale (`InstructionLocale`).
  /^exercise-instructions\/[a-z]{2,3}(-([A-Z]{2}|[0-9]{3}))?\.json$/.test(s) ||
  // ADR-0150: the exercise character limits.
  s === "limits/exercise-character-limits.yml";

// A DocumentId is a validated brand: it only decodes for a single-segment markdown
// file under an allowed root (`curricula/<name>.md` or `resources/<name>.md`), OR a
// `language-packs/`-prefixed `LanguagePackPath`, OR a `reference-guides/`-prefixed
// `ReferenceGuidePath` (both chat/studio-only — the agent's read-only `read-language-pack`
// tool and the reference-guide edit surface address their document by this shape so the
// SAME `DocumentId`-keyed machinery (conversations, turns, audit) works across document
// classes; the agent's WRITE surface stays allowlisted to `curricula/` regardless, see
// `assertAgentWritable` in `packages/core/src/agent-write.ts`). Any other path — a
// different root, a nested segment, or a traversal component — is rejected at the
// decode boundary, so the path-traversal surface is closed at the type, not inside an
// adapter (see U8 `PathOutsideAllowedRoot`).
const allowedDocumentPath = /^(curricula|resources)\/[^/]+\.md$/;

// `[^/]+` above matches `.` and `..`, so the regex alone admits `curricula/..md` and `curricula/...md`.
// Those are not exotic strings to a filesystem adapter: the stem (the filename minus `.md`) becomes a
// PATH COMPONENT there (`stripMd` → `<dir>/<stem>`), and `path.join`/`path.resolve` NORMALIZE, so those
// two ids address the stem's PARENT and GRANDPARENT directory rather than a document's own — landing, in
// the purge tool's case, on a recursive forced delete of an entire draft tree. A dots-only stem is
// therefore excluded explicitly, exactly as `LanguagePackPath` below excludes a `.`/`..` component.
const dotsOnlyStem = /^(curricula|resources)\/\.+\.md$/;

const LANGUAGE_PACK_DOCUMENT_PREFIX = "language-packs/";
const REFERENCE_GUIDE_DOCUMENT_PREFIX = "reference-guides/";

export const DocumentId = Schema.String.pipe(
  Schema.filter(
    (s) =>
      (allowedDocumentPath.test(s) && !dotsOnlyStem.test(s)) ||
      (s.startsWith(LANGUAGE_PACK_DOCUMENT_PREFIX) &&
        isValidLanguagePackPath(s.slice(LANGUAGE_PACK_DOCUMENT_PREFIX.length))) ||
      (s.startsWith(REFERENCE_GUIDE_DOCUMENT_PREFIX) &&
        isValidReferenceGuidePath(s.slice(REFERENCE_GUIDE_DOCUMENT_PREFIX.length))) ||
      "DocumentId must be a single-segment markdown file under curricula/ or resources/, a " +
        "language-packs/-prefixed language pack path, or a reference-guides/-prefixed reference " +
        "guide path, and its filename must not consist only of dots",
  ),
  Schema.brand("DocumentId"),
);
export type DocumentId = typeof DocumentId.Type;

// U8: every server route that resolves a `DocumentId` from a URL uses the `:root/:id` two-segment
// convention (`history-route.ts`, `raw-doc-route.ts`, `ws.ts`) — `:root` is the first path component,
// `:id` the rest. That "rest" is a single path segment on the wire, but for a reference-guide or
// language-pack document it carries its OWN internal `/` (e.g. `authoring-guides/doc-a.md`), so a
// caller building one of these URLs from a full `DocumentId` must percent-encode everything past the
// root — otherwise the browser splits the extra `/` into further path segments the route was never
// given, and no plain `:id` param matches them. `find-my-way-ts` (the router `HttpLayerRouter` is
// built on) is confirmed to decode a percent-encoded `/` back out of a matched segment before handing
// it to the route handler, so `encodeURIComponent` here round-trips losslessly with no server change
// needed. A curriculum id (never containing a further `/`) passes through unchanged.
//
// Takes a plain `string`, not the `DocumentId` brand: `ws-client.ts`'s `wsUrl`/`connectOnce` keep
// `documentId` as a bare string throughout (it runs inside the sync Web Worker, decoded off the wire
// rather than through a live `Schema` decode at that boundary) — a `DocumentId` is a valid `string`
// at this call, so the branded callers upstream (`document-history-client.ts`) still get full value.
export const documentIdUrlPath = (documentId: string): string => {
  const slash = documentId.indexOf("/");
  if (slash === -1) return documentId;
  return `${documentId.slice(0, slash)}/${encodeURIComponent(documentId.slice(slash + 1))}`;
};

// A `unit.cycle.module` authoring target (e.g. `3.2.1`) — the ONE way any caller scopes an exercise
// expansion to less than the whole document (ADR-0084). The studio's per-module-heading affordance and
// the `expand-exercises` tool both address a module by this target, and the server resolves it against
// head text. A validated brand (three dot-separated integers), so free text ("the greetings module",
// "unit 3") is refused at the decode boundary instead of resolving to nothing and silently escalating
// to a whole-document run — the failure mode ADR-0084 exists to close.
export const ModuleTarget = Schema.String.pipe(
  Schema.filter(
    (s) =>
      /^\d+\.\d+\.\d+$/.test(s) ||
      "ModuleTarget must be `unit.cycle.module` (three dot-separated numbers, e.g. `3.2.1`)",
  ),
  Schema.brand("ModuleTarget"),
);
export type ModuleTarget = typeof ModuleTarget.Type;

// A `LanguagePackPath` is a validated brand: a relative `.md`/`.yml` file inside
// `language-packs/language_packs/packs/{L1,L2}_<code>/`, the live editable subset carved out of the
// former reference bundle (ADR-0097, amending ADR-0021/0055's `lang/` scope) — reviewable and
// editable by a human in studio, consulted read-only by the agent through `LanguagePackStore`, never
// written by the agent. Validated by `isValidLanguagePackPath` (above, declared ahead of `DocumentId`
// so that brand can accept a `language-packs/`-prefixed path without duplicating this closure).
export const LanguagePackPath = Schema.String.pipe(
  Schema.filter(
    (s) =>
      isValidLanguagePackPath(s) ||
      "LanguagePackPath must be a relative .md/.yml file with no traversal/null-byte/percent-encoded " +
        "component, starting with an L1_<code>/ or L2_<code>/ segment",
  ),
  Schema.brand("LanguagePackPath"),
);
export type LanguagePackPath = typeof LanguagePackPath.Type;

// The six document types a language pack carries (ADR-0097) — vocab/grammar reference inventories,
// the CP (Culture & Pragmatics) authoring guide, the language profile, personas, and reference
// dialogues — plus the registry and the ADR-0120 topic files below. `cp-authoring-guide` is
// conceptually a hand-authored guide rather than per-language content data, but it shares the same
// store/port shape.
export const LanguagePackKind = Schema.Literal(
  "vocab-inventory",
  "grammar-inventory",
  "cp-authoring-guide",
  "language-profile",
  "personas",
  "reference-dialogues",
  // ADR-0108 Decision 2 — `packs/language_codes.yml`, the `CODE -> display name` registry. Unlike
  // every other kind it is not per-language: it is the file that says which languages exist, so it
  // sits at the root of the pack tree rather than under an `L1_`/`L2_` directory. It gets its own
  // kind rather than falling through to `language-profile`, because "the registry" and "a language's
  // profile" are not the same thing to anyone reading the list in studio.
  "language-registry",
  // ADR-0120 — the L2 pack's schema-validated topic files, one kind each (`personas.yml`, the ninth
  // topic, is `personas`). `checks` is the check manifest (ADR-0123).
  "orthography",
  "lexicon",
  "register",
  "grammar",
  "dialect",
  "prompt-examples",
  "rubric",
  "checks",
);
export type LanguagePackKind = typeof LanguagePackKind.Type;

// Derives `kind`/`languageCode` from a `LanguagePackPath` — pure, so both `LanguagePackStore`
// adapters (filesystem and Postgres, ADR-0009's dual-adapter split) agree on the same mapping for the
// same path rather than keeping two copies to drift. `languagePackKindOfPath` falls through to
// `"language-profile"` for a path already accepted by `LanguagePackPath`'s allowlist but not matched
// above — kept total rather than partial, so an unrecognised-but-valid path still resolves to
// something rather than throwing.
const TOPIC_FILE_KINDS: ReadonlyMap<string, LanguagePackKind> = new Map([
  ["orthography.yml", "orthography"],
  ["lexicon.yml", "lexicon"],
  ["register.yml", "register"],
  ["grammar.yml", "grammar"],
  ["dialect.yml", "dialect"],
  ["prompt_examples.yml", "prompt-examples"],
  ["rubric.yml", "rubric"],
  ["checks.yml", "checks"],
]);

export const languagePackKindOfPath = (path: LanguagePackPath): LanguagePackKind => {
  const fileName = path.split("/").pop() ?? path;
  // Checked before the filename rules below because it is matched on the WHOLE path: only the
  // root-level registry is the registry. A hypothetical `L2_QMS/language_codes.yml` would be an
  // ordinary per-language file, not this.
  if (LANGUAGE_PACK_ROOT_FILES.has(path)) return "language-registry";
  if (fileName.includes("vocab_inventory")) return "vocab-inventory";
  if (fileName.includes("grammar_inventory")) return "grammar-inventory";
  if (fileName === "cp_authoring_guide.md") return "cp-authoring-guide";
  if (fileName === "language_profile.md" || fileName === "profile.md") return "language-profile";
  if (fileName === "personas.md" || fileName === "tutors.md" || fileName === "personas.yml")
    return "personas";
  if (fileName === "reference_dialogues.md") return "reference-dialogues";
  return TOPIC_FILE_KINDS.get(fileName) ?? "language-profile";
};

// The `L1_<code>`/`L2_<code>` directory a doc lives under — the value `list(languageCode)` filters on.
//
// The root-level registry belongs to no single language, so it gets the empty string rather than a
// fake code. That keeps it out of every `list("L2_QMS")`-style query by construction: a language's
// documents are the ones under that language's directory, and the registry is not one of them. A
// caller that wants it asks for it by path.
export const languagePackCodeOfPath = (path: LanguagePackPath): string =>
  LANGUAGE_PACK_ROOT_FILES.has(path) ? "" : (path.split("/")[0] ?? "");

// The `DocumentId` a `LanguagePackPath` addresses in the audit log (U2, generalized document
// versioning) — the same `language-packs/`-prefixed shape `DocumentId`'s own filter already accepts
// above, so a pack's store row and its audit-log chain agree on one identity without either side
// re-deriving the prefix.
export const languagePackDocumentId = (path: LanguagePackPath): DocumentId =>
  DocumentId.make(`${LANGUAGE_PACK_DOCUMENT_PREFIX}${path}`);

// The inverse of `languagePackDocumentId`: the pack path a `language-packs/` document id addresses, or
// `undefined` for any other document class.
export const languagePackPathOfDocumentId = (id: DocumentId): LanguagePackPath | undefined => {
  if (!id.startsWith(LANGUAGE_PACK_DOCUMENT_PREFIX)) return undefined;
  const path = id.slice(LANGUAGE_PACK_DOCUMENT_PREFIX.length);
  return isValidLanguagePackPath(path) ? LanguagePackPath.make(path) : undefined;
};

// One language-pack document as the store holds it: content plus an optimistic-concurrency
// `revision` (bumped on every write; a stale `expectedRevision` on write fails with
// `LanguagePackConflict` rather than silently overwriting a concurrent edit) — the direct-write path
// used by the boot reconciler and any non-draft caller. Since ADR-0109 the human edit path instead
// goes through drafts/gate-2/the collab surface, same as a curriculum; OCC here is that path's
// underlying commit mechanism, not a substitute for it.
export const LanguagePackDoc = Schema.Struct({
  path: LanguagePackPath,
  documentId: DocumentId,
  kind: LanguagePackKind,
  languageCode: Schema.String,
  content: Schema.String,
  revision: Schema.Number,
  updatedAt: Schema.DateTimeUtc,
  // True when this doc's content has diverged from the git-tracked `language-packs/` file it was
  // last reconciled against (ADR-0097 Decision 2) — i.e. a studio edit exists that hasn't been
  // exported back to git yet. Always `false` on the filesystem backend (local dev), where the file
  // IS the store, so there is nothing to diverge from.
  drifted: Schema.Boolean,
});
export type LanguagePackDoc = typeof LanguagePackDoc.Type;

// The reference-guide naming transform used by the `ReferenceGuideStore` reconciler below. Maps a source-relative path under `l2_unit_plan/inputs` to its normalized
// destination name.
//
// Naming rule (ADR-0021 extended):
//   - descriptive names are kebab-cased (lowercase, `_` → `-`) — the convention ADR-0021 set for
//     the original six files;
//   - the trailing `_latest` marker is dropped (`Curriculum_Reference_latest.md` →
//     `curriculum-reference.md`), matching the `_latest` drift cleanup ADR-0021 already did;
//   - language-code directory segments (`L1_QAE`, `L2_QMS`, …) are preserved verbatim — not
//     applicable to `ReferenceGuidePath` below.
const isLanguageCode = (segment: string): boolean => /^L[12]_/.test(segment);
const kebab = (segment: string): string => segment.toLowerCase().replaceAll("_", "-");

// Source files that live in `l2_unit_plan/inputs` but are pipeline-process documentation, not
// curriculum reference a human or agent consults: `README.md` records how the generation inputs were
// authored and where each is consumed in `run_pipeline.py`, and `language_generation_steps.md` is the
// operator guide for scaffolding a new language pack. Keyed by the source-relative path
// (pre-normalization), matching what a directory scan of `l2_unit_plan/inputs` yields.
const MIRROR_EXCLUDED: ReadonlySet<string> = new Set([
  "README.md",
  "lang/language_generation_steps.md",
]);

export const isMirroredReference = (sourceRelativePath: string): boolean =>
  !MIRROR_EXCLUDED.has(sourceRelativePath);

export const normalizeReferenceName = (sourceRelativePath: string): string => {
  const segments = sourceRelativePath.split("/");
  const fileName = segments.pop();
  if (fileName === undefined || fileName.length === 0)
    throw new Error(`reference path has no file component: ${sourceRelativePath}`);

  const directories = segments.map((segment) =>
    isLanguageCode(segment) ? segment : kebab(segment),
  );

  // File name: split the stem from its full extension chain at the FIRST dot so a multi-suffix name
  // keeps a compound extension intact and only the stem is kebab-cased. Drop a trailing `_latest`
  // from the stem before kebab-casing.
  const firstDot = fileName.indexOf(".");
  const stem = firstDot === -1 ? fileName : fileName.slice(0, firstDot);
  const extension = firstDot === -1 ? "" : fileName.slice(firstDot);
  const stemWithoutLatest = stem.endsWith("_latest") ? stem.slice(0, -"_latest".length) : stem;
  const normalizedFile = `${kebab(stemWithoutLatest)}${extension.toLowerCase()}`;

  return [...directories, normalizedFile].join("/");
};

// A `ReferenceGuidePath` is a validated brand: `curriculum-reference.md`, or a `.md` file under
// `authoring-guides/` — the human-authored (not per-language, not external-standard) subset of the
// `l2_unit_plan/inputs` reference bundle (ADR-0099, amending ADR-0097/ADR-0021/ADR-0055's scope a
// second time): reviewable and editable by a human in studio, mirroring `LanguagePackPath`'s
// live-editable-store treatment. Deliberately narrow — the exclusion of the CEFR descriptors and
// the quality rubric is enforced HERE, at the brand, rather than by an ad-hoc filter list a manifest
// loader could get out of sync with. The rubric in particular has its own ADR-0055 statement that
// there is no in-app edit path; broadening this brand to include it would reverse that decision and
// needs its own call, not a silent scope creep here.
// ADR-0108 widens this brand to the WHOLE of `l2_unit_plan/inputs` that a run reads, because the
// tree is being removed from the repo and anything without a home here would simply be lost:
//
//   - `curriculum-quality-rubric.md` — ADR-0055 said there is no in-app edit path for the rubric,
//     and the comment above said reversing that "needs its own call, not a silent scope creep here".
//     Removing the tree IS that call, made explicitly: the rubric has to live somewhere, and the
//     only remaining somewhere is the store. Note this admits it to the STORE; whether studio offers
//     an edit surface for it is a separate question ADR-0055 still governs.
//   - `exercise-quality-rubric.md` — the rubric for judging a rendered exercise (CON-2093). It was
//     never under `l2_unit_plan/inputs`, only in the `reference-library` skill bundle, so the first
//     widening missed it; CON-2047 deleted the bundle copy, leaving the store as its only home.
//   - `cefr-descriptors-2020.yml` — an external standard, not human-authored, but read by
//     `sublevelsForBand` on every run.
//   - `few-shot-examples/*.jsonl` — read by the expander; `.jsonl` is line-delimited JSON, i.e.
//     text, so it stores exactly like the rest.
//
// `README.md` is deliberately still excluded (`MIRROR_EXCLUDED`): it documents how the inputs were
// authored rather than being one, so it stays a file in the repo.
//
// Validated by `isValidReferenceGuidePath` (above, declared ahead of `DocumentId` so that brand can
// accept a `reference-guides/`-prefixed path without duplicating this closure).
export const ReferenceGuidePath = Schema.String.pipe(
  Schema.filter(
    (s) =>
      isValidReferenceGuidePath(s) ||
      'ReferenceGuidePath must be "curriculum-reference.md", "curriculum-quality-rubric.md", "exercise-quality-rubric.md", "cefr-descriptors-2020.yml", an "authoring-guides/<name>.md", a "few-shot-examples/<name>.jsonl", a "workflows/<name>.yml" or an "exercise-instructions/<locale>.json" file, or "limits/exercise-character-limits.yml"',
  ),
  Schema.brand("ReferenceGuidePath"),
);
export type ReferenceGuidePath = typeof ReferenceGuidePath.Type;

// The two document types a reference guide carries — a global curriculum-shape reference and the
// per-topic authoring guides. Unlike `LanguagePackKind`'s six specific kinds, this brand's allowlist
// is narrow enough that "which of the two" is all a reader needs.
export const ReferenceGuideKind = Schema.Literal(
  "curriculum-reference",
  "authoring-guide",
  // ADR-0108 — the three classes the brand above now also admits, each named rather than folded into
  // `authoring-guide`, because none of them is one: the rubric is a judging instrument, the CEFR
  // descriptors are an external standard, and the few-shots are model exemplars.
  "quality-rubric",
  "cefr-descriptors",
  "few-shot-examples",
  // CON-1677 — a generation recipe (`workflows/<name>.yml`): which documents, prompt, model and
  // effort each l2_unit_plan step uses. Staged to runs with the guides, so a saved edit reaches the
  // next launch.
  "workflow",
  // ADR-0134 — a locale's exercise-instruction table (`exercise-instructions/<locale>.json`), edited
  // on the exercise-instructions page.
  "exercise-instructions",
  // ADR-0150 — the exercise character limits (`limits/exercise-character-limits.yml`), edited on the
  // character-limits page and read by exercise generation.
  "character-limits",
);
export type ReferenceGuideKind = typeof ReferenceGuideKind.Type;

export const referenceGuideKindOfPath = (path: ReferenceGuidePath): ReferenceGuideKind => {
  if (path === "curriculum-reference.md") return "curriculum-reference";
  if (path === "curriculum-quality-rubric.md" || path === "exercise-quality-rubric.md") {
    return "quality-rubric";
  }
  if (path === "cefr-descriptors-2020.yml") return "cefr-descriptors";
  if (path.startsWith("few-shot-examples/")) return "few-shot-examples";
  if (path.startsWith("workflows/")) return "workflow";
  if (path.startsWith("exercise-instructions/")) return "exercise-instructions";
  if (path.startsWith("limits/")) return "character-limits";
  return "authoring-guide";
};

// The `DocumentId` a `ReferenceGuidePath` addresses in the audit log — see
// `languagePackDocumentId` above, same rationale, one document class over.
export const referenceGuideDocumentId = (path: ReferenceGuidePath): DocumentId =>
  DocumentId.make(`${REFERENCE_GUIDE_DOCUMENT_PREFIX}${path}`);

// The inverse of `referenceGuideDocumentId`: `undefined` for any `DocumentId` outside the
// `reference-guides/` namespace. Safe to `.make` unchecked — `DocumentId`'s own brand filter already ran
// `isValidReferenceGuidePath` on this exact suffix before admitting the id.
export const referenceGuidePathOfDocumentId = (
  documentId: DocumentId,
): ReferenceGuidePath | undefined =>
  documentId.startsWith(REFERENCE_GUIDE_DOCUMENT_PREFIX)
    ? ReferenceGuidePath.make(documentId.slice(REFERENCE_GUIDE_DOCUMENT_PREFIX.length))
    : undefined;

// CON-1677: a generation recipe (`workflow` kind) is a reference guide the generic edit paths
// refuse. Every other guide is editable by any viewer (ADR-0108's 2026-09-28 amendment); a recipe
// changes what every later launch reads, so only a studio-admin edits it, on the recipe page, whose
// `PUT /api/recipes/doc` also checks the pipeline could load it. Called at the same seams the
// retired read-only check used: the REST PUT, WS draft-open, WS restore-version, and agent writes.
// ADR-0134 refuses an exercise-instruction table at the same seams: it too is edited only on its own
// page, which checks it is a well-formed table. ADR-0150 refuses the exercise character limits the
// same way, edited only on /character-limits. The names predate those kinds.
export class RecipeEditedElsewhere extends Data.TaggedError("RecipeEditedElsewhere")<{
  readonly documentId: DocumentId;
}> {
  override get message(): string {
    const path = referenceGuidePathOfDocumentId(this.documentId);
    const kind = path === undefined ? undefined : referenceGuideKindOfPath(path);
    if (kind === "exercise-instructions") {
      return `${this.documentId} is an exercise-instruction table — edit it on the exercise instructions page (/exercise-instructions)`;
    }
    if (kind === "character-limits") {
      return `${this.documentId} is the exercise character limits — edit them on the character limits page (/character-limits)`;
    }
    return `${this.documentId} is the generation recipe — a studio admin edits it on the recipe page (/recipe)`;
  }
}

export const assertNotRecipe = (
  documentId: DocumentId,
): Effect.Effect<void, RecipeEditedElsewhere> => {
  const path = referenceGuidePathOfDocumentId(documentId);
  const kind = path === undefined ? undefined : referenceGuideKindOfPath(path);
  return kind === "workflow" || kind === "exercise-instructions" || kind === "character-limits"
    ? Effect.fail(new RecipeEditedElsewhere({ documentId }))
    : Effect.void;
};

// One reference-guide document as the store holds it — the same optimistic-concurrency shape as
// `LanguagePackDoc` (see that comment: OCC governs the direct-write path, drafts/collab the human-edit
// path since ADR-0109), minus `languageCode` (these documents are not per-language).
export const ReferenceGuideDoc = Schema.Struct({
  path: ReferenceGuidePath,
  documentId: DocumentId,
  kind: ReferenceGuideKind,
  content: Schema.String,
  revision: Schema.Number,
  updatedAt: Schema.DateTimeUtc,
  // See `LanguagePackDoc.drifted` — same meaning, against the `l2_unit_plan/inputs` git file.
  drifted: Schema.Boolean,
});
export type ReferenceGuideDoc = typeof ReferenceGuideDoc.Type;

// A request-scoped viewer identity (ADR-0013), decoded from the JWT `sub` claim (or, later, the
// OIDC subject). It is **attacker-held material** — a `Schema.UUID` brand, not an arbitrary string —
// so a forged/oversized `sub` is rejected at the decode boundary and can never reach display-name
// derivation, logs, or a future authz key. It is the server-side identity and is **never** carried on
// the presence wire (broadcasts use `ConnectionId` + derived display fields only).
export const ViewerId = Schema.UUID.pipe(Schema.brand("ViewerId"));
export type ViewerId = typeof ViewerId.Type;

// The opaque per-connection presence key (ADR-0013): one WS connection mints one `ConnectionId`
// (`crypto.randomUUID()`), and presence is keyed by it, never by `ViewerId`. Connection-keying makes
// the two-tab and reconnect cases correct by construction — a leave on one connection cannot evict
// another's entry — and keeps the auth subject off the wire. A brand so it cannot be confused with a
// `ViewerId` or arbitrary text.
export const ConnectionId = Schema.String.pipe(Schema.brand("ConnectionId"));
export type ConnectionId = typeof ConnectionId.Type;

// The document roots that are seeded and listed as live documents (R18), the single source of truth
// shared by every store/log/seed adapter. Only `curricula/` now: the rubric and glossary are no
// longer log-backed documents (ADR-0055) — they ship as static `data/resources/*.md` files. The `DocumentId` brand still *recognizes* the retired `resources/`
// shape (above) as defense-in-depth — the agent write-guards refuse any `resources/` id
// (`ResourceNotAgentEditable`) — but nothing seeds, lists, or creates one.
export const DEFAULT_DOCUMENT_ROOTS: readonly string[] = ["curricula/"];

// Every root with a collab surface (ADR-0109): the roots the live `DocumentStore` must READ and
// WRITE, as opposed to `DEFAULT_DOCUMENT_ROOTS` above, which is what is seeded and LISTED as the
// curricula index. The two diverged the moment ADR-0109 gave reference guides and language packs
// drafts — a store scoped to `DEFAULT_DOCUMENT_ROOTS` refuses every land on those two roots — so they
// are named separately rather than one being widened into the other.
// Whether a document's bytes are markdown, and so whether the write-boundary formatter (ADR-0079,
// `normalizeWrite`) may touch them. Every curriculum is `.md`; a language pack or reference guide can
// be `.yml` (or `.jsonl`), and running YAML through a MARKDOWN formatter rewrites it: each `#` comment
// line parses as a heading, so the formatter inserts a blank line after every one and collapses
// `#   <CODE>` to `# <CODE>` — a silent rewrite of the file on every save. The extension is the
// document's own declaration of its format; nothing else in the id carries it.
export const isMarkdownDocument = (documentId: string): boolean => documentId.endsWith(".md");

export const COLLAB_DOCUMENT_ROOTS: readonly string[] = [
  "curricula/",
  "reference-guides/",
  "language-packs/",
];
